import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patientFileHighlightsRoot } from '@/hooks/useDocumentHighlights';
import { isDocumentInfoSchemaError } from '@/lib/documentInfo';
import { supabase } from '@/lib/supabase';

export interface DocumentInfoRecord {
  status: 'ready';
  title: string;
  date: string;
  author: string;
}

export type DocumentInfoResult = DocumentInfoRecord | { status: 'schema_missing' };

export const documentInfoKey = (documentId: string | null | undefined) =>
  ['documents', 'info', documentId ?? null] as const;

export function useDocumentInfo(documentId: string | null | undefined) {
  return useQuery({
    queryKey: documentInfoKey(documentId),
    enabled: Boolean(documentId),
    queryFn: async (): Promise<DocumentInfoResult> => {
      if (!documentId) throw new Error('missing_document');
      const { data, error } = await supabase
        .from('documents')
        .select('document_date, document_title, document_author')
        .eq('id', documentId)
        .single();
      if (error) {
        if (isDocumentInfoSchemaError(error)) return { status: 'schema_missing' };
        throw error;
      }
      return {
        status: 'ready',
        title: data.document_title ?? '',
        author: data.document_author ?? '',
        date: (data.document_date ?? '').slice(0, 10),
      };
    },
  });
}

type UpdatePatientDocumentRpc = {
  rpc: (
    fn: 'update_patient_document',
    args: {
      p_document_id: string;
      p_document_date: string;
      p_document_title: string;
      p_document_author: string;
      p_set_document_title: boolean;
      p_set_document_author: boolean;
    },
  ) => PromiseLike<{ error: { code?: string; message?: string } | null }>;
};

export function useSaveDocumentInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      documentId: string;
      title: string;
      date: string;
      author: string;
    }): Promise<void> => {
      // Must stay a method call. Copying `supabase.rpc` into a local drops
      // `this`, and the client throws before the request is sent.
      const { error } = await (supabase as unknown as UpdatePatientDocumentRpc).rpc(
        'update_patient_document',
        {
          p_document_id: input.documentId,
          p_document_date: input.date,
          p_document_title: input.title,
          p_document_author: input.author,
          p_set_document_title: true,
          p_set_document_author: true,
        },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: patientFileHighlightsRoot });
    },
  });
}
